// frontend/src/pages/NewsDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicPostApi } from '../api/content.api';
import { POST_TYPE_LABELS } from '../utils/constants';
import { toJalaliFull } from '../utils/jalali';
import Card from '../components/common/Card';
import Icon from '../components/common/Icon';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/v1\/?$/, '');

const TYPE_ICONS = {
  NEWS: 'news',
  EVENT: 'calendar',
  BLOG: 'document',
  ARTICLE: 'document',
  ANNOUNCEMENT: 'bell',
};

const TYPE_COLORS = {
  NEWS: 'bg-blue-50 text-blue-700 border-blue-200',
  EVENT: 'bg-amber-50 text-amber-700 border-amber-200',
  BLOG: 'bg-violet-50 text-violet-700 border-violet-200',
  ARTICLE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ANNOUNCEMENT: 'bg-rose-50 text-rose-700 border-rose-200',
};

function mediaUrl(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_ORIGIN}${url}`;
}

/**
 * رندر سبک Markdown به JSX، بدون نیاز به کتابخانه‌ی خارجی.
 * پشتیبانی: ## تیتر, - لیست, ![alt](url), [text](url), **bold**, *italic*
 */
function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-black text-text-dark">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return (
        <em key={key} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={key}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-emerald-600 underline underline-offset-2 hover:text-emerald-700"
        >
          {linkMatch[1]}
        </a>
      );
    }
    return part;
  });
}

function renderMarkdown(content) {
  if (!content) return null;
  const lines = content.split('\n');
  const blocks = [];
  let listBuffer = [];

  function flushList(key) {
    if (listBuffer.length > 0) {
      blocks.push(
        <ul key={`ul-${key}`} className="my-3 list-inside list-disc space-y-1 text-slate-700">
          {listBuffer.map((item, i) => (
            <li key={i}>{renderInline(item, `li-${key}-${i}`)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  }

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      flushList(idx);
      blocks.push(
        <img
          key={`img-${idx}`}
          src={mediaUrl(imgMatch[2])}
          alt={imgMatch[1] || ''}
          className="my-5 w-full rounded-2xl object-cover"
        />
      );
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList(idx);
      blocks.push(
        <h2 key={`h-${idx}`} className="mt-6 mb-3 text-xl font-black text-text-dark">
          {renderInline(trimmed.slice(3), `h-${idx}`)}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith('- ')) {
      listBuffer.push(trimmed.slice(2));
      return;
    }

    flushList(idx);

    if (trimmed === '') {
      blocks.push(<div key={`sp-${idx}`} className="h-2" />);
      return;
    }

    blocks.push(
      <p key={`p-${idx}`} className="leading-9 text-slate-700">
        {renderInline(line, `p-${idx}`)}
      </p>
    );
  });

  flushList('end');
  return blocks;
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse">
      <div className="h-72 rounded-3xl bg-slate-100" />
      <div className="mt-6 h-8 w-2/3 rounded bg-slate-100" />
      <div className="mt-4 space-y-3">
        <div className="h-4 w-full rounded bg-slate-100" />
        <div className="h-4 w-full rounded bg-slate-100" />
        <div className="h-4 w-3/4 rounded bg-slate-100" />
      </div>
    </div>
  );
}

export default function NewsDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);

    getPublicPostApi(id)
      .then(({ data }) => {
        if (!mounted) return;
        setPost(data.data);
      })
      .catch(() => {
        if (!mounted) return;
        setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  const typeColor = post ? TYPE_COLORS[post.type] || 'bg-slate-50 text-slate-600 border-slate-200' : '';

  return (
    <section dir="rtl" className="min-h-screen py-16 animate-fade-in">
      <div className="container-page">
        <div className="mb-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 transition hover:text-emerald-600"
          >
            <Icon name="arrowLeft" className="h-4 w-4 rotate-180" />
            بازگشت به اخبار و مطالب
          </Link>
        </div>

        {loading ? (
          <DetailSkeleton />
        ) : notFound || !post ? (
          <div className="mx-auto flex min-h-[420px] max-w-xl flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white px-6 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-slate-100 text-slate-400">
              <Icon name="news" className="h-9 w-9" />
            </div>
            <h3 className="mt-5 text-lg font-black text-slate-800">این مطلب پیدا نشد</h3>
            <p className="mt-2 max-w-md text-sm leading-7 text-slate-400">
              ممکن است این مطلب حذف شده یا هنوز منتشر نشده باشد.
            </p>
            <Link
              to="/news"
              className="mt-5 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-600"
            >
              بازگشت به لیست مطالب
            </Link>
          </div>
        ) : (
          <Card className="mx-auto max-w-3xl overflow-hidden rounded-3xl border border-slate-100 bg-white p-0">
            <div className="relative h-72 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-emerald-800 sm:h-96">
              {post.coverImage ? (
                <>
                  <img src={mediaUrl(post.coverImage)} alt={post.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </>
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Icon name={TYPE_ICONS[post.type] || 'news'} className="h-20 w-20 text-white/20" />
                </div>
              )}
              <div className="absolute right-4 top-4">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold ${typeColor}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {POST_TYPE_LABELS[post.type] || post.type}
                </span>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {post.category?.title && (
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold text-blue-600">
                    {post.category.title}
                  </span>
                )}
                {post.publishDate && (
                  <span className="text-[11px] text-slate-400">{toJalaliFull(post.publishDate)}</span>
                )}
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                  <Icon name="eye" className="h-3.5 w-3.5" />
                  {post.viewCount || 0} بازدید
                </span>
              </div>

              <h1 className="text-2xl font-black leading-10 text-text-dark sm:text-3xl">{post.title}</h1>

              {post.summary && <p className="mt-4 text-base leading-8 text-slate-500">{post.summary}</p>}

              <div className="mt-6">{renderMarkdown(post.content)}</div>

              {post.gallery && post.gallery.length > 0 && (
                <div className="mt-8 border-t border-slate-100 pt-6">
                  <p className="mb-3 text-sm font-bold text-slate-500">گالری تصاویر</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {post.gallery.map((g) => (
                      <img
                        key={g.id}
                        src={mediaUrl(g.url)}
                        alt=""
                        className="h-32 w-full rounded-xl object-cover"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <Icon name="userGroup" className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-bold text-slate-500">{post.author?.fullName || 'ناشناس'}</span>
                </div>
              </div>

              {post.tags && post.tags.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {post.tags.map((tag) => (
                    <span key={tag.id} className="rounded-full bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-500">
                      #{tag.title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </section>
  );
}
